import ExcelJS from 'exceljs';
import { toast } from 'sonner';
import * as XLSX from 'xlsx';

export type SampleFileFormat = 'excel' | 'csv';

/**
 * Field definition for sample template generation
 */
export interface SampleFieldDefinition {
  id: string; // Column header; dotted for a leaf under a nested field (`cpt_codes.code`)
  placeholder?: string; // Example value for the field
  options?: { value: string; label: string }[]; // Available options for dropdown (select fields)
  required?: boolean; // Field is always required
  conditionallyRequired?: boolean; // Field becomes required based on another field's value
  repeated?: boolean; // Belongs to a repeated field, so the cell accepts `|`-separated values
}

// Header style constants for the Excel sample template.
// Required: amber fill, Conditional: light-blue fill. Both use bold black text.
const REQUIRED_HEADER_FILL = {
  type: 'pattern',
  pattern: 'solid',
  fgColor: { argb: 'FFFFF3CD' },
} as const;
const CONDITIONAL_HEADER_FILL = {
  type: 'pattern',
  pattern: 'solid',
  fgColor: { argb: 'FFDDEBF7' },
} as const;
const BOLD_BLACK_FONT = { bold: true, color: { argb: 'FF000000' } } as const;

const requirementLabelFor = (f: SampleFieldDefinition): string => {
  if (f.required) return 'required';
  if (f.conditionallyRequired) return 'conditional';
  return 'optional';
};

const headerFillFor = (
  f: SampleFieldDefinition,
): typeof REQUIRED_HEADER_FILL | typeof CONDITIONAL_HEADER_FILL | undefined => {
  if (f.required) return REQUIRED_HEADER_FILL;
  if (f.conditionallyRequired) return CONDITIONAL_HEADER_FILL;
  return undefined;
};

// Enum sentinels that carry no information for someone filling in a template.
const PLACEHOLDER_ENUM_SUFFIXES = ['_UNSPECIFIED', '_UNKNOWN'];

const isSentinelOption = (opt: { value: string; label: string }): boolean =>
  PLACEHOLDER_ENUM_SUFFIXES.some(suffix => opt.value.toUpperCase().endsWith(suffix));

/**
 * Example value for a column. Enum/select fields carry no `placeholder` in the
 * schema (only free-text fields do), which left choice columns such as
 * facility_type and *_network_status blank in the sample. Fall back to a real
 * option so every column shows a usable value; the label is used because the
 * Excel dropdown lists labels.
 */
export const sampleValueFor = (f: SampleFieldDefinition): string => {
  if (f.placeholder) return f.placeholder;
  const options = f.options ?? [];
  if (options.length === 0) return '';
  const option = options.find(opt => !isSentinelOption(opt)) ?? options[0];
  return option.label || option.value;
};

/**
 * Downloads a sample template for a workflow in the specified format
 * Uses ExcelJS for Excel format (supports data validation dropdowns)
 * Uses SheetJS (xlsx) for CSV format (simpler, no data validation needed)
 *
 * @param fields - Array of field definitions with id, placeholder, and options
 * @param format - File format ('excel' or 'csv')
 * @param orgName - Organization name for filename
 * @param programName - Program name for filename
 * @returns Promise<void>
 */
export const downloadWorkflowSample = async (
  fields: SampleFieldDefinition[],
  format: SampleFileFormat,
  orgName?: string,
  programName?: string,
): Promise<void> => {
  if (fields.length === 0) {
    toast.error('No workflow fields available to generate sample');
    return;
  }

  // Generate filename with org, program, and campaign_sample
  const cleanOrgName = orgName?.replace(/\s+/g, '_').toLowerCase() || 'org';
  const cleanProgramName = programName?.replace(/\s+/g, '_').toLowerCase() || 'program';
  const extension = format === 'csv' ? 'csv' : 'xlsx';
  const filename = `${cleanOrgName}_${cleanProgramName}_campaign_sample.${extension}`;

  if (format === 'csv') {
    // Use SheetJS for CSV (simpler, no data validation needed)
    const wb = XLSX.utils.book_new();

    // Extract headers and example row from field definitions
    const headers = fields.map(f => f.id);
    const exampleRow = fields.map(sampleValueFor);

    // Create sample data with headers and example row
    const sampleData = [headers, exampleRow];

    // Convert to worksheet
    const ws = XLSX.utils.aoa_to_sheet(sampleData);

    // Add the worksheet to the workbook
    XLSX.utils.book_append_sheet(wb, ws, 'Sample');

    // Write the file and trigger download
    XLSX.writeFile(wb, filename, { bookType: 'csv' });
  } else {
    // Use ExcelJS for Excel (supports data validation dropdowns)
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Sample');

    // Row 1: column headers (cell value is the field id so upload validation matches)
    const headers = fields.map(f => f.id);
    worksheet.addRow(headers);

    // Row 2: requirement label per column (required / conditional / optional).
    // processExcelFile detects and skips this row on re-upload.
    worksheet.addRow(fields.map(requirementLabelFor));

    // Row 3: example values
    worksheet.addRow(fields.map(sampleValueFor));

    // Apply requirement-driven styling to rows 1 and 2.
    [1, 2].forEach(rowNumber => {
      worksheet.getRow(rowNumber).eachCell((cell, colNumber) => {
        const fill = headerFillFor(fields[colNumber - 1]);
        if (fill) {
          cell.fill = fill;
          cell.font = BOLD_BLACK_FONT;
        }
      });
    });

    // Collect fields that have dropdown options
    const fieldsWithOptions = fields
      .map((field, colIndex) => ({ field, colIndex }))
      .filter(({ field }) => field.options && field.options.length > 0);

    // Write options to a hidden reference sheet to avoid the 255-char inline formula limit
    if (fieldsWithOptions.length > 0) {
      const refSheet = workbook.addWorksheet('_DropdownData', {
        state: 'veryHidden',
      });

      fieldsWithOptions.forEach(({ field, colIndex }, refColIndex) => {
        const labels = field.options!.map(opt => opt.label || opt.value);
        // Write each label in a row of this reference column
        labels.forEach((label, rowIndex) => {
          refSheet.getCell(rowIndex + 1, refColIndex + 1).value = label;
        });

        // Reference the range in the hidden sheet for data validation
        const colLetter = String.fromCharCode(65 + refColIndex); // A, B, C, ...
        const rangeRef = `'_DropdownData'!$${colLetter}$1:$${colLetter}$${labels.length}`;

        // Attach dropdown to the example row (row 3, below header + label rows)
        const cell = worksheet.getCell(3, colIndex + 1);
        cell.dataValidation = {
          type: 'list',
          allowBlank: true,
          formulae: [rangeRef],
          showErrorMessage: false,
        };
      });
    }

    // Generate and download file
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });

    // Trigger download
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  const formatName = format === 'csv' ? 'CSV' : 'Excel';
  toast.success(`Sample ${formatName} template downloaded`);
};
